/**
 * Exit Intent Detection System
 * 
 * Detects when user intends to leave the page based on mouse movements
 * Requirements:
 * - Only show once per month (cookie-based)
 * - Only show after 30 seconds on the page
 * - Detect exit intent via mouse leaving viewport at top
 */

(function($) {
    'use strict';

    const ExitIntent = {
        // Configuration
        config: {
            cookieName: 'uc_exit_intent_shown',
            cookieExpireDays: 30,
            timeOnPageSeconds: 4,
            mouseSensitivity: 20, // pixels from top to trigger
        },

        // State
        state: {
            hasBeenShown: false,
            timeOnPageMet: false,
            isEnabled: false,
        },

        /**
         * Initialize the exit intent detection
         */
        init: function() {
            // Check if already shown this month
            if (this.hasShownRecently()) {
                console.log('[Exit Intent] Already shown this month, skipping initialization');
                return;
            }

            // Start time tracking
            this.startTimeTracking();
            
            console.log('[Exit Intent] Initialized - waiting for 30 seconds of page time');
        },

        /**
         * Check if exit intent was already shown in the last 30 days
         */
        hasShownRecently: function() {
            return this.getCookie(this.config.cookieName) === 'true';
        },

        /**
         * Start tracking time on page
         */
        startTimeTracking: function() {
            const self = this;
            
            setTimeout(function() {
                self.state.timeOnPageMet = true;
                self.enableExitIntent();
                console.log('[Exit Intent] 30 seconds elapsed - exit intent detection now active');
            }, this.config.timeOnPageSeconds * 1000);
        },

        /**
         * Enable exit intent detection
         */
        enableExitIntent: function() {
            if (this.state.isEnabled) return;
            
            this.state.isEnabled = true;
            const self = this;

            // Desktop: Detect mouse leaving viewport at top
            $(document).on('mouseleave.exitIntent', function(e) {
                // Check if mouse is leaving from the top of the viewport
                if (e.clientY < self.config.mouseSensitivity) {
                    self.handleExitIntent();
                }
            });

            // Additional detection: mouse moving towards top with high velocity
            let lastY = 0;
            let lastTime = Date.now();

            $(document).on('mousemove.exitIntent', function(e) {
                const currentY = e.clientY;
                const currentTime = Date.now();
                const timeDiff = currentTime - lastTime;
                
                // Only check if enough time has passed
                if (timeDiff > 50) {
                    const velocityY = (lastY - currentY) / timeDiff;
                    
                    // Fast upward movement near top of page
                    if (velocityY > 1 && currentY < 100) {
                        self.handleExitIntent();
                    }
                    
                    lastY = currentY;
                    lastTime = currentTime;
                }
            });

            console.log('[Exit Intent] Mouse tracking enabled');
        },

        /**
         * Handle exit intent detection
         */
        handleExitIntent: function() {
            // Prevent multiple triggers
            if (this.state.hasBeenShown) return;
            
            // Double-check requirements
            if (!this.state.timeOnPageMet) return;
            if (this.hasShownRecently()) return;

            this.state.hasBeenShown = true;
            
            // Disable further detection
            this.disableExitIntent();
            
            // Set cookie to prevent showing again for 30 days
            this.setCookie(this.config.cookieName, 'true', this.config.cookieExpireDays);
            
            console.log('[Exit Intent] Exit intent detected! Triggering action...');
            
            // Trigger custom event that other scripts can listen to
            $(document).trigger('exitIntentDetected');
            
            // You can add your actual action here
            // For example: show a modal, display a popup, etc.
            this.showExitIntentAction();
        },

        /**
         * Disable exit intent detection
         */
        disableExitIntent: function() {
            this.state.isEnabled = false;
            $(document).off('.exitIntent');
            console.log('[Exit Intent] Detection disabled');
        },

        /**
         * Show exit intent action
         */
        showExitIntentAction: function() {
            console.log('[Exit Intent] Showing popup');
            
            const $popup = $('#exit-intent-popup');
            
            if ($popup.length === 0) {
                console.warn('[Exit Intent] Popup element not found');
                return;
            }
            
            // Show popup
            $popup.show();
            
            // Trigger animation
            setTimeout(function() {
                $popup.addClass('is-visible');
                $('body').addClass('exit-intent-popup-open');
            }, 10);
            
            // Handle close button
            $popup.find('.exit-intent-popup__close').on('click', function() {
                ExitIntent.closePopup();
            });
            
            // Handle dismiss button
            $popup.find('.exit-intent-popup__dismiss').on('click', function() {
                ExitIntent.closePopup();
            });
            
            // Handle overlay click
            $popup.find('.exit-intent-popup__overlay').on('click', function() {
                ExitIntent.closePopup();
            });
            
            // Handle ESC key
            $(document).on('keydown.exitIntentPopup', function(e) {
                if (e.key === 'Escape') {
                    ExitIntent.closePopup();
                }
            });
            
            // Track in analytics if available
            if (typeof gtag !== 'undefined') {
                gtag('event', 'exit_intent_triggered', {
                    'event_category': 'engagement'
                });
            }
        },

        /**
         * Close popup
         */
        closePopup: function() {
            const $popup = $('#exit-intent-popup');
            
            $popup.removeClass('is-visible');
            $('body').removeClass('exit-intent-popup-open');
            
            setTimeout(function() {
                $popup.hide();
            }, 300);
            
            // Clean up event listeners
            $(document).off('keydown.exitIntentPopup');
            
            console.log('[Exit Intent] Popup closed');
        },

        /**
         * Set a cookie
         */
        setCookie: function(name, value, days) {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            const expires = "expires=" + date.toUTCString();
            document.cookie = name + "=" + value + ";" + expires + ";path=/;SameSite=Lax";
        },

        /**
         * Get a cookie value
         */
        getCookie: function(name) {
            const nameEQ = name + "=";
            const ca = document.cookie.split(';');
            for (let i = 0; i < ca.length; i++) {
                let c = ca[i];
                while (c.charAt(0) === ' ') c = c.substring(1, c.length);
                if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
            }
            return null;
        },

        /**
         * Delete a cookie (useful for testing)
         */
        deleteCookie: function(name) {
            this.setCookie(name, '', -1);
        },

        /**
         * Reset for testing purposes
         * Call this from browser console: window.ExitIntent.reset()
         */
        reset: function() {
            this.deleteCookie(this.config.cookieName);
            this.disableExitIntent();
            this.state.hasBeenShown = false;
            this.state.timeOnPageMet = false;
            console.log('[Exit Intent] Reset complete. Reload page to test again.');
        }
    };

    // Initialize on document ready
    $(document).ready(function() {
        ExitIntent.init();
    });

    // Expose to window for testing/debugging
    window.ExitIntent = ExitIntent;

    // Example: Listen for the exit intent event elsewhere in your code
    // $(document).on('exitIntentDetected', function() {
    //     console.log('Exit intent event received!');
    //     // Your custom handling here
    // });

})(jQuery);

